// v2.0.0 - 2026-02-01 - Member Energy page with charts

import { useState, useEffect } from 'react'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { EnergyChart } from '@/components/charts'

interface HourlyData {
  date: string
  hour: number
  immessa: number
  prelevata: number
  eaci: number
}

interface EnergyData {
  member: {
    id: string
    name: string
    pod: string
  }
  period: {
    year: number
    month: number
  }
  hourlyData: HourlyData[]
  summary: {
    totalImmessa: number
    totalPrelevata: number
    totalEaci: number
    avgDailyImmessa: number
    avgDailyPrelevata: number
  }
  comparison: {
    cerAvgImmessa: number
    cerAvgPrelevata: number
    percentileImmessa: number
    percentilePrelevata: number
  }
}

const MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
]

export default function MemberEnergy() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedMonth, setSelectedMonth] = useState(currentMonth > 1 ? currentMonth - 1 : 12)
  const [timeRange, setTimeRange] = useState<'hourly' | 'daily'>('daily')
  const [data, setData] = useState<EnergyData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const years = [currentYear, currentYear - 1, currentYear - 2]

  useEffect(() => {
    fetchEnergyData()
  }, [selectedYear, selectedMonth])

  const fetchEnergyData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(`/api/member/energy?year=${selectedYear}&month=${selectedMonth}`)
      if (!response.ok) throw new Error('Errore nel caricamento dati')
      const result = await response.json()
      setData(result.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore')
    } finally {
      setIsLoading(false)
    }
  }

  const formatNumber = (n: number, decimals: number = 2) => {
    return n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  }

  const formatKwh = (n: number) => `${formatNumber(n)} kWh`

  return (
    <PageContainer>
      <PageHeader title="I tuoi Dati Energia" description="Analizza i tuoi consumi e produzione" />

      {/* Period Selection */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Anno</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)]"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mese</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)]"
              >
                {MONTHS.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Visualizzazione</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as 'hourly' | 'daily')}
                className="h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)]"
              >
                <option value="daily">Giornaliera</option>
                <option value="hourly">Oraria</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent)]"></div>
        </div>
      ) : error || !data ? (
        <Card>
          <CardContent className="py-12 text-center text-[var(--color-error)]">
            {error || 'Nessun dato disponibile per questo periodo'}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[var(--color-text-secondary)]">Energia Immessa</p>
                <p className="text-2xl font-bold text-green-500">{formatKwh(data.summary.totalImmessa)}</p>
                <p className="text-xs text-[var(--color-text-tertiary)]">
                  Media: {formatKwh(data.summary.avgDailyImmessa)}/giorno
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[var(--color-text-secondary)]">Energia Prelevata</p>
                <p className="text-2xl font-bold text-blue-500">{formatKwh(data.summary.totalPrelevata)}</p>
                <p className="text-xs text-[var(--color-text-tertiary)]">
                  Media: {formatKwh(data.summary.avgDailyPrelevata)}/giorno
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-[var(--color-text-secondary)]">EACI Contribuito</p>
                <p className="text-2xl font-bold text-amber-500">{formatKwh(data.summary.totalEaci)}</p>
                <p className="text-xs text-[var(--color-text-tertiary)]">min(Immessa, Prelevata) per ora</p>
              </CardContent>
            </Card>
          </div>

          {/* Energy Chart */}
          <div className="mb-6">
            <EnergyChart
              data={data.hourlyData}
              title={`Andamento Energia - ${MONTHS[selectedMonth - 1]} ${selectedYear}`}
              description="Confronto tra energia immessa, prelevata e condivisa (EACI)"
              timeRange={timeRange}
            />
          </div>

          {/* Comparison with CER */}
          <Card>
            <CardHeader>
              <CardTitle>Confronto con la CER</CardTitle>
              <CardDescription>Come ti posizioni rispetto agli altri membri</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium mb-2">Produzione (Immessa)</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Tu</span>
                      <span className="font-bold">{formatKwh(data.summary.totalImmessa)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[var(--color-text-secondary)]">
                      <span>Media CER</span>
                      <span>{formatKwh(data.comparison.cerAvgImmessa)}</span>
                    </div>
                    <div className="w-full bg-[var(--color-bg-tertiary)] rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${Math.min(100, data.comparison.percentileImmessa)}%` }}
                      />
                    </div>
                    <p className="text-xs text-[var(--color-text-tertiary)]">
                      Sei nel {data.comparison.percentileImmessa}° percentile
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Consumo (Prelevata)</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Tu</span>
                      <span className="font-bold">{formatKwh(data.summary.totalPrelevata)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[var(--color-text-secondary)]">
                      <span>Media CER</span>
                      <span>{formatKwh(data.comparison.cerAvgPrelevata)}</span>
                    </div>
                    <div className="w-full bg-[var(--color-bg-tertiary)] rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.min(100, data.comparison.percentilePrelevata)}%` }}
                      />
                    </div>
                    <p className="text-xs text-[var(--color-text-tertiary)]">
                      Sei nel {data.comparison.percentilePrelevata}° percentile
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  )
}
